import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getFirestore, type Firestore } from 'firebase/firestore';
import { getFunctions, type Functions } from 'firebase/functions';
import { getStorage, type FirebaseStorage } from 'firebase/storage';
import { getAuth, signInAnonymously, onAuthStateChanged, type Auth } from 'firebase/auth';

/**
 * Firebase client SDK.
 * Config values are pulled from Vite env vars (VITE_ prefix = client-safe).
 * All secrets and admin-only work happens in /functions (server-side).
 */

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let app: FirebaseApp | null = null;
let db: Firestore | null = null;
let functions: Functions | null = null;
let storage: FirebaseStorage | null = null;

export function getFirebaseApp(): FirebaseApp {
  if (!app) {
    app = initializeApp(firebaseConfig);
  }
  return app;
}

export function getDb(): Firestore {
  if (!db) db = getFirestore(getFirebaseApp());
  return db;
}

export function getFns(): Functions {
  if (!functions) {
    // Region: us-east4 (Ashburn) — closest low-latency region for Florida
    functions = getFunctions(getFirebaseApp(), 'us-east4');
  }
  return functions;
}

export function getStore(): FirebaseStorage {
  if (!storage) storage = getStorage(getFirebaseApp());
  return storage;
}

let auth: Auth | null = null;

export function getAuthInstance(): Auth {
  if (!auth) auth = getAuth(getFirebaseApp());
  return auth;
}

/**
 * Anonymous sign-in bootstrap.
 *
 * Phase 1 has no customer accounts, but every Firestore rule is written as
 * `request.auth != null` — the kiosk, the waiting view, /tv and the booking
 * wizard all read shop state. Without a signed-in anonymous session those
 * reads are denied and every surface renders empty.
 *
 * Resolves once a session exists, so callers can await it before subscribing.
 */
export function ensureAnonymousAuth(): Promise<void> {
  const a = getAuthInstance();
  return new Promise((resolve, reject) => {
    const unsub = onAuthStateChanged(a, (user) => {
      if (user) {
        unsub();
        resolve();
        return;
      }
      signInAnonymously(a).catch(reject);
    });
  });
}
